import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { api } from "../api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Users, Building2, Clock, BookOpen, BarChart3, CalendarDays,
  ShieldCheck, LogOut, UserCog, ArrowRight, MessageCircle, Receipt, ClipboardList, Key, FileText, Car, IdCard, QrCode, HeartPulse, FileCheck2, Wallet, Calculator, Package,
} from "lucide-react";
import { ModuleArt } from "../components/ModuleArt";
import "./Hub.css";

const MODULES = [
  { key: "candidates", title: "Candidates", icon: Users, accent: "#3b82f6",
    to: "/candidates", statKey: "candidates_total", statLabel: "candidates" },
  { key: "clients", title: "Clients", icon: Building2, accent: "#f59e0b",
    to: "/clients", statKey: "clients_total", statLabel: "clients" },
  { key: "calendar", title: "Calendar", icon: CalendarDays, accent: "#10b981",
    to: "/calendar", statKey: "appointments_open", statLabel: "open appointments" },
  { key: "kedatangan", title: "Kedatangan", icon: Clock, accent: "#8b5cf6",
    to: "/kedatangan", statKey: "roster_workers_total", statLabel: "workers tracked" },
  { key: "stock", title: "Stock", icon: Package, accent: "#0ea5e9",
    to: "/stock", statKey: "stock_items_total", statLabel: "items tracked" },
  { key: "jnj", title: "J&J", icon: QrCode, accent: "#dc2626",
    to: "/jnj", statKey: "jnj_scans_today", statLabel: "scans today" },
  { key: "fitlah", title: "FitLah", icon: HeartPulse, accent: "#ec4899",
    to: "/fitlah", statKey: "fitlah_alert", statLabel: "wellness app" },
  { key: "permits", title: "Permits", icon: FileCheck2, accent: "#0ea5e9",
    to: "/permits", statKey: "permits_alert", statLabel: "renewals due" },
  { key: "payouts", title: "Payouts", icon: Wallet, accent: "#f43f5e",
    to: "/payouts", statKey: "payouts_pending", statLabel: "fees pending" },
  { key: "accounting", title: "Accounting", icon: Calculator, accent: "#059669",
    to: "/accounting", statKey: "accounting_net", statLabel: "RM net this month" },
  { key: "bills", title: "Bills & Payments", icon: Receipt, accent: "#f97316",
    to: "/bills", statKey: "bills_outstanding", statLabel: "RM outstanding" },
  { key: "tasks", title: "Tasks", icon: ClipboardList, accent: "#0ea5e9",
    to: "/tasks", statKey: "tasks_open", statLabel: "open tasks" },
  { key: "invoice", title: "Invoice", icon: FileText, accent: "#eab308",
    to: "/invoice", statKey: "invoice_badge", statLabel: "need action" },
  { key: "vehicles", title: "Vehicles", icon: Car, accent: "#14b8a6",
    to: "/vehicles", statKey: "vehicles_overdue", statLabel: "need attention" },
  { key: "workers", title: "Workers Register", icon: IdCard, accent: "#a855f7",
    to: "/workers", statKey: "workers_total", statLabel: "registered" },
  { key: "book", title: "Book", icon: BookOpen, accent: "#06b6d4",
    to: "/book", statKey: "book_entries", statLabel: "entries" },
  { key: "analytics", title: "Analytics", icon: BarChart3, accent: "#ec4899",
    to: "/analytics", statKey: "active_staff", statLabel: "active staff" },
  { key: "chat", title: "Chat", icon: MessageCircle, accent: "#60a5fa",
    to: "/chat", statKey: "chat_unread", statLabel: "unread" },
];

const ADMIN_CARD = {
  key: "staff", title: "Staff Management", icon: UserCog, accent: "#ef4444",
  to: "/staff", statKey: "pending_batch_requests", statLabel: "pending",
};

export default function Hub() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [pwOpen, setPwOpen] = useState(false);

  const loadStats = useCallback(async () => {
    try { const { data } = await api.get("/hub/stats"); setStats(data || {}); } catch { /* silent */ }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { const t = setInterval(loadStats, 10000); return () => clearInterval(t); }, [loadStats]);

  // Subtle pointer-tracked parallax for the grid. Math: translate up to ±6px
  // / rotate up to ±0.6deg based on cursor offset from page center.
  const gridRef = useRef(null);
  useEffect(() => {
    const node = gridRef.current;
    if (!node) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const handler = (e) => {
      const w = window.innerWidth, h = window.innerHeight;
      const dx = (e.clientX / w - 0.5) * 2;  // -1..1
      const dy = (e.clientY / h - 0.5) * 2;
      node.style.transform =
        `translate3d(${(-dx * 6).toFixed(2)}px, ${(-dy * 6).toFixed(2)}px, 0)
         rotateX(${(dy * 0.6).toFixed(2)}deg)
         rotateY(${(-dx * 0.6).toFixed(2)}deg)`;
    };
    window.addEventListener("pointermove", handler, { passive: true });
    return () => window.removeEventListener("pointermove", handler);
  }, []);

  if (!user) return null;
  const isAdmin = user.role === "admin";
  const allowed = (m) => isAdmin || (user.modules_allowed || []).includes(m.key);
  const visible = MODULES.filter(allowed);
  const onSignOut = async () => { await logout(); navigate("/login"); };

  return (
    <div className="hub-bg min-h-screen text-slate-100 flex flex-col relative" data-testid="hub-page">
      <header className="sticky top-0 z-40 border-b hairline bg-[#0a0e1a]/70 backdrop-blur-md relative">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-3 flex items-center gap-3">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-md bg-blue-600/90 flex items-center justify-center">
              <ShieldCheck className="w-4 h-4 text-white" />
            </div>
            <div className="leading-tight">
              <div className="text-sm font-semibold tracking-tight">Mercy Management System</div>
              <div className="text-[10px] text-slate-500 num hidden sm:block">Licence JTKSM 928</div>
            </div>
            <span className={`ml-2 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider num
              ${isAdmin ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                        : "bg-slate-500/10 text-slate-300 border border-slate-500/30"}`}>
              {isAdmin ? "Admin" : "Staff"}
            </span>
          </div>
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full border hairline surface-2" data-testid="user-badge">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: user.color }} />
              <span className="text-sm">{user.display_name}</span>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setPwOpen(true)}
              className="text-slate-300 hover:text-white hover:bg-white/5" data-testid="change-password-button">
              <Key className="w-4 h-4 mr-1.5" /> Password
            </Button>
            <Button variant="ghost" size="sm" onClick={onSignOut}
              className="text-slate-300 hover:text-white hover:bg-white/5" data-testid="sign-out-button">
              <LogOut className="w-4 h-4 mr-1.5" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-[1400px] mx-auto w-full px-4 md:px-6 py-10 md:py-14 relative">
        <div ref={gridRef} className="hub-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {visible.map((m) => {
            let badge = null, badgeTone = null;
            if (m.key === "bills" && stats.bills_overdue > 0) {
              badge = `${stats.bills_overdue} overdue`; badgeTone = "red";
            } else if (m.key === "tasks" && stats.tasks_overdue > 0) {
              badge = `${stats.tasks_overdue} overdue`; badgeTone = "red";
            } else if (m.key === "invoice" && stats.invoice_badge > 0) {
              badge = `${stats.invoice_badge}`; badgeTone = "red";
            } else if (m.key === "vehicles" && stats.vehicles_overdue > 0) {
              badge = `${stats.vehicles_overdue} overdue`; badgeTone = "red";
            } else if (m.key === "vehicles" && stats.vehicles_attention > 0) {
              badge = `${stats.vehicles_attention} soon`; badgeTone = "amber";
            } else if (m.key === "workers" && stats.workers_expiring_urgent > 0) {
              badge = `${stats.workers_expiring_urgent} expiring`; badgeTone = "red";
            } else if (m.key === "workers" && stats.workers_expiring_soon > 0) {
              badge = `${stats.workers_expiring_soon} soon`; badgeTone = "amber";
            } else if (m.key === "payouts" && stats.payouts_overdue > 0) {
              badge = `${stats.payouts_overdue} overdue`; badgeTone = "red";
            } else if (m.key === "accounting" && stats.accounting_overdue > 0) {
              badge = `${stats.accounting_overdue} overdue`; badgeTone = "red";
            } else if (m.key === "accounting" && stats.accounting_due_soon > 0) {
              badge = `${stats.accounting_due_soon} due soon`; badgeTone = "amber";
            } else if (m.key === "stock" && stats.stock_low > 0) {
              badge = `${stats.stock_low} low stock`; badgeTone = "red";
            } else if (m.key === "stock" && stats.stock_pending > 0) {
              badge = `${stats.stock_pending} pending`; badgeTone = "amber";
            }
            let statNode = null;
            if (m.key === "accounting") {
              const net = Number(stats.accounting_net || 0);
              const positive = net >= 0;
              statNode = (
                <>
                  <span className={`text-2xl font-semibold num tabular-nums ${positive ? "text-emerald-300" : "text-red-300"}`}>
                    {positive ? "+" : "−"} RM {Math.abs(net).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                  <span className="text-[11px] text-slate-500">net this month</span>
                </>
              );
            }
            return (
              <ModuleCard key={m.key} m={m} stat={stats[m.statKey]}
                badge={badge} badgeTone={badgeTone} statNode={statNode} />
            );
          })}
          {isAdmin && (
            <ModuleCard
              m={ADMIN_CARD}
              stat={stats[ADMIN_CARD.statKey]}
              badge={stats.pending_batch_requests > 0 ? stats.pending_batch_requests : null}
            />
          )}
        </div>

        {visible.length === 0 && !isAdmin && (
          <div className="rounded-lg border hairline surface-1 p-10 text-center">
            <div className="text-slate-300">You do not have access to any modules yet.</div>
            <div className="text-xs text-slate-500 mt-1">Please contact your administrator.</div>
          </div>
        )}
      </main>

      <footer className="border-t hairline py-4 text-[11px] text-slate-500 num text-center">
        Mercy Management System
      </footer>

      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </div>
  );
}

function ChangePasswordDialog({ open, onOpenChange }) {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.current_password || !form.new_password) {
      toast.error("Fill current and new password"); return;
    }
    if (form.new_password.length < 6) {
      toast.error("New password must be at least 6 characters"); return;
    }
    if (form.new_password !== form.confirm) {
      toast.error("Passwords don't match"); return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password updated");
      setForm({ current_password: "", new_password: "", confirm: "" });
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-sm"
        data-testid="change-password-dialog">
        <DialogHeader><DialogTitle>Change password</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-400">Current password</Label>
            <Input type="password" autoComplete="current-password" value={form.current_password}
              onChange={(e) => setForm({ ...form, current_password: e.target.value })}
              className="bg-[#0a0e1a] border-slate-800 mt-1"
              data-testid="current-password" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">New password (min 6 chars)</Label>
            <Input type="password" autoComplete="new-password" value={form.new_password}
              onChange={(e) => setForm({ ...form, new_password: e.target.value })}
              className="bg-[#0a0e1a] border-slate-800 mt-1"
              data-testid="new-password" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Confirm new password</Label>
            <Input type="password" autoComplete="new-password" value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })}
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
              className="bg-[#0a0e1a] border-slate-800 mt-1"
              data-testid="confirm-password" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-blue-600 hover:bg-blue-500"
            data-testid="change-password-submit">Update password</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ModuleCard({ m, stat, badge, badgeTone, statNode }) {
  const Icon = m.icon;
  const isAlert = badgeTone === "red";
  const isWarn  = badgeTone === "amber";
  const tone = isAlert
    ? "bg-red-500/30 text-red-100 border-red-500/60"
    : "bg-amber-500/20 text-amber-300 border-amber-500/30";
  const badgeSuffix = isAlert ? "" : " pending";

  // Click ripple — toggles a CSS class for 600ms; CSS picks up the radial
  // gradient positioned at the click point via custom properties --rx/--ry.
  const onClick = (e) => {
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    el.style.setProperty("--rx", `${e.clientX - rect.left}px`);
    el.style.setProperty("--ry", `${e.clientY - rect.top}px`);
    el.classList.remove("is-rippling");
    // Force a reflow so the animation restarts even on rapid double-click
    void el.offsetWidth;
    el.classList.add("is-rippling");
    setTimeout(() => el.classList.remove("is-rippling"), 600);
  };

  return (
    <Link
      to={m.to}
      onClick={onClick}
      data-testid={`hub-card-${m.key}`}
      data-alert={isAlert ? "true" : undefined}
      data-warn={isWarn && !isAlert ? "true" : undefined}
      className={`hub-card ripple group relative rounded-xl border hairline surface-1 p-6 overflow-hidden min-h-[170px] ${
        isAlert ? "hub-card-alert" : (isWarn ? "hub-card-warn" : "")
      }`}
      style={{ "--accent": m.accent }}
    >
      {/* Themed module illustration — sits behind the content, picks up the
       * card's accent via `color`. Opacity rises on hover for delight. */}
      <ModuleArt moduleKey={m.key} style={{ color: m.accent }} />
      <div
        aria-hidden
        className="absolute -right-10 -top-10 w-40 h-40 rounded-full opacity-[0.04] group-hover:opacity-[0.09] transition-opacity"
        style={{ background: m.accent }}
      />
      <div className="relative flex items-start justify-between">
        <div
          className="w-11 h-11 rounded-lg flex items-center justify-center"
          style={{ background: m.accent + "22", color: m.accent }}
        >
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex items-center gap-2">
          {badge != null && (
            <span
              data-testid={`hub-badge-${m.key}`}
              className={`num text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${tone} ${isAlert ? "hub-badge-alert" : ""}`}
            >
              {isAlert && <span className="hub-pulse-dot mr-1" aria-hidden />}
              {badge}{badgeSuffix}
            </span>
          )}
          <ArrowRight className="w-4 h-4 text-slate-600 group-hover:text-blue-400 group-hover:translate-x-0.5 transition-all" />
        </div>
      </div>
      {/* Title + stat — constrained to ~55% width so the 3D illustration on
       * the right has room to breathe and never overlaps the numbers. */}
      <div className="relative mt-6 pr-[120px] sm:pr-[140px]">
        <div className="text-lg font-semibold tracking-tight">{m.title}</div>
        {statNode != null ? (
          <div className="flex items-baseline gap-1.5 mt-1">{statNode}</div>
        ) : stat != null && (
          <div className="flex items-baseline gap-1.5 mt-1">
            <span className="text-2xl font-semibold num tabular-nums">{stat}</span>
            <span className="text-[11px] text-slate-500">{m.statLabel}</span>
          </div>
        )}
      </div>
    </Link>
  );
}
